// @flow
import * as React from 'react';
import { useContext } from 'react';
import Moderate from './moderate/Moderate';
import SecondOpinion from './second-opinion/SecondOpinion';
import type { ContextRouter } from 'react-router-dom';
import LandingPage from 'pages/__components__/LandingPage';
import ReviewSearch from 'containers/search/NativeReviews';
import MediaSearch from 'containers/search/Media';
import NetworkReviewSearch from 'containers/search/NetworkReviews';
import NetworkQuestionSearch from 'containers/search/NetworkQuestions';
import QuestionSearch from 'containers/search/Questions';
import AnswerSearch from 'containers/search/Answers';
import { PowerReviewsModerationCard } from 'components';
import { type CoreDatum } from 'ducks/coreDataReducer';
import {
  updateObservations,
  requestObservationsUpdate,
} from 'ducks/coreDataActions';
import { UserGeneratedTextHighlighter } from 'components/ContentSection/UserGeneratedTextContainer';
import { PwrContentCardProvider } from 'context/content-card-context';
import { ClientContext } from 'containers/client/ClientProvider';
import {
  ReviewSearch as PwrDistroReviewSearch,
  MediaSearch as PwrDistroMediaSearch,
  QuestionSearch as PwrDistroQuestionSearch,
  AnswerSearch as PwrDistroAnswerSearch,
} from 'containers/search/PwrDistribution';
import { getIsPwrDistroClient } from 'components/ModerationCard/ClientModerationCard/utils';

const moderateRoute = {
  exact: true,
  path: 'moderate',
  component: Moderate,
  label: 'Moderate',
  navItem: true,
  clientContextRequired: false,
};

const secondOpinionRoute = {
  exact: true,
  path: 'second-opinion',
  component: SecondOpinion,
  label: 'Second Opinion',
  navItem: true,
  clientContextRequired: false,
};

const searchRoutes = [
  'sep',
  {
    exact: false,
    path: 'native-reviews',
    label: ReviewSearch.label,
    render: props => <ReviewSearch {...props} CardComponent={PwrCard} />,
    navItem: true,
  },
  {
    exact: false,
    path: 'network-reviews',
    label: NetworkReviewSearch.label,
    render: props => (
      <NetworkReviewSearch hideCopy {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
  {
    exact: false,
    path: 'images-and-video',
    label: MediaSearch.label,
    render: props => <MediaSearch {...props} CardComponent={PwrCard} />,
    navItem: true,
  },
  {
    exact: false,
    path: 'questions',
    label: QuestionSearch.label,
    render: props => <QuestionSearch {...props} CardComponent={PwrCard} />,
    navItem: true,
  },
  {
    exact: false,
    path: 'network-questions',
    label: NetworkQuestionSearch.label,
    render: props => (
      <NetworkQuestionSearch hideCopy {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
  {
    exact: false,
    path: 'answers',
    label: 'Answers',
    render: props => <AnswerSearch {...props} CardComponent={PwrCard} />,
    navItem: true,
  },
];

const pwrDistroRedirectRoutes = [
  {
    exact: false,
    path: 'native-reviews',
    render: props => <ReviewSearch {...props} CardComponent={PwrCard} />,
    navItem: false,
  },
  {
    exact: false,
    path: 'network-reviews',
    render: props => (
      <NetworkReviewSearch hideCopy {...props} CardComponent={PwrCard} />
    ),
    navItem: false,
  },
  {
    exact: false,
    path: 'images-and-video',
    render: props => <MediaSearch {...props} CardComponent={PwrCard} />,
    navItem: false,
  },
  {
    exact: false,
    path: 'questions',
    render: props => <QuestionSearch {...props} CardComponent={PwrCard} />,
    navItem: false,
  },
  {
    exact: false,
    path: 'network-questions',
    render: props => (
      <NetworkQuestionSearch hideCopy {...props} CardComponent={PwrCard} />
    ),
    navItem: false,
  },
  {
    exact: false,
    path: 'answers',
    render: props => <AnswerSearch {...props} CardComponent={PwrCard} />,
    navItem: false,
  },
];

const pwrDistroSearchRoutes = [
  'sep',
  {
    exact: false,
    path: 'reviews-search',
    label: 'Reviews',
    render: props => (
      <PwrDistroReviewSearch {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
  {
    exact: false,
    path: 'media-search',
    label: 'Images & Videos',
    render: props => (
      <PwrDistroMediaSearch {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
  {
    exact: false,
    path: 'questions-search',
    label: 'Questions',
    render: props => (
      <PwrDistroQuestionSearch {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
  {
    exact: false,
    path: 'answers-search',
    label: 'Answers',
    render: props => (
      <PwrDistroAnswerSearch {...props} CardComponent={PwrCard} />
    ),
    navItem: true,
  },
];

function getRoutesForUser(clientId, clientProps, userInfo: { ...UserInfo }) {
  const { access, role } = userInfo;
  const routes = [];

  if (access.moderate) {
    routes.push(moderateRoute);
  }

  if (access.secondOpinion) {
    routes.push(secondOpinionRoute);
  }

  if (role.moderationAdmin) {
    if (getIsPwrDistroClient(clientId, clientProps)) {
      routes.push(...pwrDistroSearchRoutes, ...pwrDistroRedirectRoutes);
    } else {
      routes.push(
        ...searchRoutes,
        ...pwrDistroSearchRoutes.map(r => ({ ...r, navItem: false }))
      );
    }
  }

  return routes;
}

export default (props: { ...UserInfo, ...ContextRouter }) => {
  const clientProps = useContext(ClientContext);
  const clientId = clientProps ? clientProps.clientId : null;

  return (
    <PwrContentCardProvider>
      <UserGeneratedTextHighlighter>
        <LandingPage
          label="Moderation"
          routes={getRoutesForUser(clientId, clientProps, props)}
          landingRoute={'moderate'}
          {...props}
        />
      </UserGeneratedTextHighlighter>
    </PwrContentCardProvider>
  );
};

//
// PwrCard
//
type PwrCardProps = {
  datum: CoreDatum,
  dispatch: Function,
};

class PwrCard extends React.Component<PwrCardProps> {
  onChange: Function = (...rest) =>
    this.props.dispatch(updateObservations(...rest));
  onSubmit: Function = (...rest) =>
    this.props.dispatch(requestObservationsUpdate(...rest));

  render() {
    const {
      ugc,
      moderationState,
      subject,
      observationRequestStatus: submitStatus,
    } = this.props.datum;

    return (
      <PowerReviewsModerationCard
        product={subject}
        ugc={ugc}
        moderationState={moderationState}
        status={submitStatus}
        onChange={this.onChange}
        onSubmit={this.onSubmit}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/moderation/index.js